import { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, ChevronLeft, FileText, Trash2, Zap, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { format, parseISO, addDays, isAfter, isBefore } from 'date-fns';
import { saveShiftPrep, getMyToolsData, deleteToolItem, getBmsEvents, GetMyToolsDataOutputType, GetBmsEventsOutputType } from 'zite-endpoints-sdk';
import ShiftPrepForm, { ShiftFormData, defaultShiftFormData, WeatherInfo } from '@/components/tools/ShiftPrepForm';
import ShiftPrepOutput from '@/components/tools/ShiftPrepOutput';

type Saved = GetMyToolsDataOutputType['shiftPreps'][0];
type BmsEvent = GetBmsEventsOutputType['events'][0];
type View = 'list' | 'form' | 'output';

function getWeatherIcon(code: number) {
  if (code === 0) return '☀️';
  if (code <= 3) return '⛅';
  if (code <= 48) return '🌫️';
  if (code <= 67) return '🌧️';
  if (code <= 77) return '❄️';
  if (code <= 82) return '🌦️';
  return '⛈️';
}
function getWeatherDesc(code: number) {
  if (code === 0) return 'Clear sky';
  if (code <= 3) return 'Partly cloudy';
  if (code <= 48) return 'Foggy';
  if (code <= 67) return 'Rainy';
  if (code <= 77) return 'Snowy';
  if (code <= 82) return 'Showery';
  return 'Stormy';
}
function getWeatherAdvice(code: number, temp: number): string[] {
  const a: string[] = [];
  if (temp > 20) { a.push('Hot day — expect increased garden/decking demand'); a.push('Check if tailored shorts are permitted for staff'); }
  if (code >= 51 && code <= 82) { a.push('Rain expected — prepare wet floor mats, umbrella stand, move outdoor covers'); }
  if (temp < 8) { a.push('Cold day — light fires early, check heating is on, warm welcome for guests'); }
  if (code === 0 && temp > 15) a.push('Sunny — set up outdoor area early, expect garden demand');
  return a;
}

export default function ShiftPrep() {
  const { user } = useAuth();
  const [view, setView] = useState<View>('list');
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<ShiftFormData>({ ...defaultShiftFormData });
  const [savedId, setSavedId] = useState<string | null>(null);
  const [saved, setSaved] = useState<Saved[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [weather, setWeather] = useState<WeatherInfo | null>(null);
  const [bmsEvents, setBmsEvents] = useState<BmsEvent[]>([]);
  const [bmsConnected, setBmsConnected] = useState(false);

  useEffect(() => {
    fetch('https://api.open-meteo.com/v1/forecast?latitude=51.59&longitude=-2.16&current_weather=true&daily=temperature_2m_max,precipitation_sum&timezone=Europe%2FLondon&forecast_days=1')
      .then(r => r.json())
      .then(d => {
        const code = d.current_weather?.weathercode ?? 0;
        const temp = d.daily?.temperature_2m_max?.[0] ?? d.current_weather?.temperature ?? 15;
        setWeather({ description: getWeatherDesc(code), temp: Math.round(temp), advice: getWeatherAdvice(code, temp), icon: getWeatherIcon(code) });
      }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    getBmsEvents({ daysAhead: 21 })
      .then(d => {
        setBmsConnected(d.connected);
        const now = new Date();
        const cutoff = addDays(now, 21);
        setBmsEvents(d.events.filter(e => {
          if (!e.date) return true;
          const d2 = new Date(e.date);
          return isAfter(d2, now) && isBefore(d2, cutoff);
        }).slice(0, 8));
      })
      .catch(() => {});
  }, [user]);

  const loadSaved = useCallback(() => {
    if (!user) return;
    getMyToolsData({}).then(d => setSaved(d.shiftPreps.filter(s => s.status !== 'Archived')));
  }, [user]);

  useEffect(() => { loadSaved(); }, [loadSaved]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = (field: keyof ShiftFormData, value: any) =>
    setFormData(p => ({ ...p, [field]: value }));

  const startNew = () => {
    const w = weather;
    setFormData({
      ...defaultShiftFormData,
      managerInCharge: `${user?.preferredName || user?.firstName || ''} ${user?.lastName || ''}`.trim(),
      weatherDescription: w?.description || '',
      weatherAdvice: w?.advice || [],
      weatherTemp: w?.temp || 0,
    });
    setSavedId(null);
    setStep(1);
    setView('form');
  };

  const startFromBmsEvent = (event: BmsEvent) => {
    const w = weather;
    const guestStr = event.guestCount || '';
    setFormData({
      ...defaultShiftFormData,
      managerInCharge: `${user?.preferredName || user?.firstName || ''} ${user?.lastName || ''}`.trim(),
      shiftDate: event.date || defaultShiftFormData.shiftDate,
      shiftType: 'Event',
      expectedCovers: guestStr,
      eventsToday: `${event.name}${event.clientName ? ` — ${event.clientName}` : ''}`,
      eventTimes: event.startTime && event.endTime ? `${event.startTime} – ${event.endTime}` : '',
      eventLocations: event.location || '',
      specialMenus: event.menu || '',
      preOrders: event.preOrders || '',
      allergens: event.allergens || '',
      weatherDescription: w?.description || '',
      weatherAdvice: w?.advice || [],
      weatherTemp: w?.temp || 0,
    });
    setSavedId(null);
    setStep(1);
    setView('form');
    toast.success(`Prep sheet started for: ${event.name}`);
  };

  const loadExisting = (s: Saved) => {
    try {
      const data = JSON.parse(s.formData);
      setFormData(data);
      setSavedId(s.id);
      setStep(1);
      setView('form');
    } catch { toast.error('Could not load'); }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const dateStr = formData.shiftDate ? format(parseISO(formData.shiftDate), 'EEE do MMM') : 'Shift';
      const title = `${dateStr} — ${formData.shiftType}`;
      const res = await saveShiftPrep({
        id: savedId || undefined, title, shiftDate: formData.shiftDate,
        shiftType: formData.shiftType, formData: JSON.stringify(formData), status: 'Final',
      });
      if (!savedId) setSavedId(res.id);
      toast.success('Shift prep sheet saved!');
      loadSaved();
    } catch { toast.error('Failed to save'); } finally { setIsSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteToolItem({ type: 'shift', id, action: 'archive' });
      toast.success('Archived');
      loadSaved();
    } catch { toast.error('Failed to archive'); }
  };

  if (view === 'output') return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => setView('list')} className="text-muted-foreground hover:text-foreground"><ChevronLeft className="w-5 h-5" /></button>
        <h1 className="text-2xl font-bold">Shift Prep Sheet</h1>
      </div>
      <ShiftPrepOutput data={formData} onEdit={() => { setStep(3); setView('form'); }} onSave={handleSave} isSaving={isSaving} />
    </div>
  );

  if (view === 'form') return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => setView('list')} className="text-muted-foreground hover:text-foreground"><ChevronLeft className="w-5 h-5" /></button>
        <h1 className="text-2xl font-bold">Build Shift Prep</h1>
      </div>
      <Card className="p-6">
        <ShiftPrepForm step={step} formData={formData} onChange={handleChange} weather={weather}
          onNext={() => setStep(s => Math.min(s + 1, 3))}
          onPrev={() => setStep(s => Math.max(s - 1, 1))}
          onGenerate={() => setView('output')} />
      </Card>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/tools" className="text-muted-foreground hover:text-foreground"><ChevronLeft className="w-5 h-5" /></Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Shift Prep Builder</h1>
          <p className="text-sm text-muted-foreground">Build printable shift prep sheets with weather, events and staff plans</p>
        </div>
        <Button onClick={startNew}><Plus className="w-4 h-4 mr-1.5" /> New Shift Prep</Button>
      </div>

      {weather && (
        <div className="flex items-center gap-2 p-3 bg-muted/40 border border-border rounded-xl mb-4 text-sm">
          <span className="text-xl">{weather.icon}</span>
          <span className="font-medium">{weather.description}</span>
          <span className="text-muted-foreground">{weather.temp}°C today</span>
          {weather.advice[0] && <span className="text-muted-foreground hidden md:block">— {weather.advice[0]}</span>}
        </div>
      )}

      {/* BMS Live Events Panel */}
      {bmsConnected && bmsEvents.length > 0 && (
        <Card className="mb-6 border-primary/30 overflow-hidden">
          <div className="px-4 py-3 bg-primary/5 border-b border-border flex items-center gap-2">
            <Zap className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-foreground">Upcoming Events from BMS</span>
            <span className="text-xs text-muted-foreground ml-auto">Next 21 days · Live data</span>
          </div>
          <div className="divide-y divide-border">
            {bmsEvents.map(event => (
              <div key={event.id} className="px-4 py-3 flex items-center gap-3 hover:bg-muted/30 transition-colors">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{event.name}</p>
                  <div className="flex items-center gap-2 flex-wrap mt-0.5">
                    {event.date && (
                      <span className="text-xs text-muted-foreground">
                        {format(parseISO(event.date), 'EEE do MMM')}
                      </span>
                    )}
                    {event.startTime && (
                      <span className="text-xs text-muted-foreground">{event.startTime}{event.endTime ? ` – ${event.endTime}` : ''}</span>
                    )}
                    {event.location && (
                      <Badge variant="outline" className="text-xs h-4">{event.location}</Badge>
                    )}
                    {event.guestCount && (
                      <span className="text-xs text-muted-foreground">{event.guestCount} guests</span>
                    )}
                    {event.clientName && (
                      <span className="text-xs text-muted-foreground">· {event.clientName}</span>
                    )}
                    {event.allergens && (
                      <Badge variant="destructive" className="text-xs h-4">⚠️ Allergens</Badge>
                    )}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => startFromBmsEvent(event)} className="shrink-0">
                  Create Prep →
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {!bmsConnected && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-muted/30 border border-dashed border-border rounded-xl text-xs text-muted-foreground">
          <ExternalLink className="w-3.5 h-3.5 shrink-0" />
          <span>Connect to BMS Hub to see upcoming events here automatically. Contact your manager to configure the BMS bridge.</span>
        </div>
      )}

      {saved.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <h3 className="font-semibold text-lg mb-1">No shift prep sheets yet</h3>
          <p className="text-muted-foreground text-sm mb-4">Create your first shift prep sheet to get started</p>
          <Button onClick={startNew}><Plus className="w-4 h-4 mr-1.5" /> Create Shift Prep</Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {saved.map(s => (
            <Card key={s.id} className="p-4 flex items-center gap-4 hover:border-primary/40 transition-colors">
              <FileText className="w-8 h-8 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{s.title}</p>
                <p className="text-xs text-muted-foreground">{s.shiftDate ? format(parseISO(s.shiftDate), 'EEE do MMM yyyy') : ''} {s.shiftType && `· ${s.shiftType}`}</p>
              </div>
              <Badge variant={s.status === 'Final' ? 'default' : 'outline'} className="text-xs shrink-0">{s.status}</Badge>
              <div className="flex gap-1 shrink-0">
                <Button variant="outline" size="sm" onClick={() => loadExisting(s)}>Open</Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(s.id)}><Trash2 className="w-3.5 h-3.5 text-muted-foreground" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
